import { useEffect, useState } from 'react';
import { Header, Table, Input, Button, Icon, Label, Pagination, Modal, Message, Field } from 'react-frontier';
import { useBreadcrumbs, useUser } from '../Hooks';
import { PromoterActivityType, SetLoading } from '../SiteClasses';
import { bindClick, bindFormChange } from '../SiteUtil';
import UserAccess from '../UserAccess';
import Validator from '../Validator';
import Toast from '../Toast';
import API from '../API';

const PAGE_SIZE = 40;

enum Modals{
	CREATE = 1,
	EDIT = 2,
	DELETE = 3,
}

interface ActivityTypeForm{
	type_name: string,
}

var ActivityTypesList = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var canCreate = hasAccess(UserAccess.ACTIVITIES.CREATE_ACTIVITY_TYPES);
	var canEdit = hasAccess(UserAccess.ACTIVITIES.EDIT_ACTIVITY_TYPES);
	var canDelete = hasAccess(UserAccess.ACTIVITIES.DELETE_ACTIVITY_TYPES);
	var [types, setTypes] = useState<PromoterActivityType[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [filter, setFilter] = useState<string>('');
	var [page, setPage] = useState<number>(0);

	var [shownModal, setShownModal] = useState<number>(null);
	var [selectedType, setSelectedType] = useState<PromoterActivityType>(null);
	var [form, setForm] = useState<ActivityTypeForm>({ type_name: '' });
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);

	useEffect(()=>{
		setTitle('Tipos de actividad');
		get();
	}, []);

	var get = ()=>{
		setLoading(true);
		API.getActivityTypes().then(res=>{
			if(res.error) return setLoadError(res.message);
			setTypes(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error al obtener los tipos de actividad. (GATE-1)');
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onSearchChange = (val: string)=>{
		setFilter(val);
		setPage(0);
	}

	var onCreate = ()=>{
		setForm({ type_name: '' });
		setModalPrompts(null);
		setShownModal(Modals.CREATE);
	}

	var onEdit = (type: PromoterActivityType)=>{
		setSelectedType(type);
		setForm({ type_name: type.type_name });
		setModalPrompts(null);
		setShownModal(Modals.EDIT);
	}

	var onToggleDelete = (type: PromoterActivityType)=>{
		setSelectedType(type);
		setModalPrompts(null);
		setShownModal(Modals.DELETE);
	}

	var create = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			type_name: [{ rule: 'empty', label: 'Nombre del tipo' }]
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.createActivityType(form.type_name.trim()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Tipo de actividad creado exitosamente');
			setTypes(prev=>[...(prev || []), { activity_type_id: res.data.activity_type_id, type_name: form.type_name.trim(), deleted: false }]);
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado creando el tipo de actividad. (CATE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var edit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			type_name: [{ rule: 'empty', label: 'Nombre del tipo' }]
		});
		setModalPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.editActivityType(selectedType.activity_type_id, form.type_name.trim()).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Tipo de actividad actualizado exitosamente');
			setTypes(prev=>prev.map(t=>t.activity_type_id === selectedType.activity_type_id ? { ...t, type_name: form.type_name.trim() } : t));
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado actualizando el tipo de actividad. (EATE-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var submitToggleDelete = (setLoading: SetLoading)=>{
		setModalPrompts(null);
		setLoading(true);

		var is_restore = !!selectedType.deleted;
		var request = is_restore ? API.restoreActivityType(selectedType.activity_type_id) : API.deleteActivityType(selectedType.activity_type_id);
		request.then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success(is_restore ? 'Tipo de actividad restaurado exitosamente' : 'Tipo de actividad eliminado exitosamente');
			setTypes(prev=>prev.map(t=>t.activity_type_id === selectedType.activity_type_id ? { ...t, deleted: !is_restore } : t));
			setShownModal(null);
		}).catch(()=>{
			setModalPrompts([`Hubo un error inesperado ${is_restore ? 'restaurando' : 'eliminando'} el tipo de actividad. (${is_restore ? 'RATE' : 'DATE'}-1)`]);
		}).finally(()=>{
			setLoading(false);
		});
	}

	var onFormChange = bindFormChange(form, setForm);

	if(loadError) return <Header text='Error' subtext={loadError} />;
	if(!types) return <Header text='Cargando datos' loading />;

	var filtered = !filter.trim() ? types : types.filter(t=>t.type_name.toLowerCase().includes(filter.toLowerCase()));
	var shown_types = filtered.slice(page * PAGE_SIZE, (page * PAGE_SIZE) + PAGE_SIZE);

	return <div>
		<Table
			style={{ width: 650, margin: 'auto', marginTop: 20 }}
			striped
		>
			<Table.Head
				title='Tipos de actividad'
				titleRight={canCreate ? (
					<Button iconName='plus' color='black' text='Agregar' size='small' onClick={onCreate} />
				) : undefined}
			>
				<Table.Row>
					<Table.Cell colSpan={4} style={{ padding: 8 }}>
						<Input fluid placeholder='Buscar...' value={filter} onChange={onSearchChange} iconName='search' style={{ margin: 0 }} />
					</Table.Cell>
				</Table.Row>
				<Table.Row>
					<Table.Cell header collapsing>ID</Table.Cell>
					<Table.Cell header>Nombre</Table.Cell>
					<Table.Cell header collapsing>Estado</Table.Cell>
					<Table.Cell header collapsing centered><Icon name='wrench' /></Table.Cell>
				</Table.Row>
			</Table.Head>
			<Table.Body>
				{loading ? (
					<Table.Row>
						<Table.Cell colSpan={4} centered>
							<Header loading text='Cargando datos' />
						</Table.Cell>
					</Table.Row>
				) : shown_types.length === 0 ? (
					<Table.Row>
						<Table.Cell colSpan={4} centered>
							No se encontraron registros
						</Table.Cell>
					</Table.Row>
				) : (
					shown_types.map(t=>(
						<Table.Row key={t.activity_type_id}>
							<Table.Cell collapsing>{t.activity_type_id}</Table.Cell>
							<Table.Cell>{t.type_name}</Table.Cell>
							<Table.Cell collapsing>
								<Label color={t.deleted ? 'red' : 'green'}>{t.deleted ? 'Inactivo' : 'Activo'}</Label>
							</Table.Cell>
							<Table.Cell collapsing centered className='no-select'>
								{(canEdit || canDelete) ? (
									<Button basic icon iconName='ellipsis' size='small' menu={[
										{ text: 'Editar', iconName: 'pen', onClick: ()=>onEdit(t), if: canEdit },
										t.deleted
											? { text: 'Restaurar', iconName: 'trash-restore', onClick: ()=>onToggleDelete(t), if: canDelete }
											: { text: 'Eliminar', iconName: 'trash', onClick: ()=>onToggleDelete(t), if: canDelete },
									]} />
								) : <Icon name='minus' />}
							</Table.Cell>
						</Table.Row>
					))
				)}
			</Table.Body>
		</Table>
		{(filtered.length > PAGE_SIZE || page !== 0) && (
			<div style={{ width: 650, margin: 'auto', marginTop: 10 }}>
				<Pagination
					page={page}
					pageCount={Math.ceil(filtered.length / PAGE_SIZE)}
					onPageChange={setPage}
				/>
			</div>
		)}

		<Modal open={shownModal===Modals.CREATE} onClose={bindClick(setShownModal, null)} size="tiny">
			<Modal.Header text='Crear tipo de actividad' />
			<Modal.Content>
				<Field label='Nombre del tipo'>
					<Input
						fluid
						value={form.type_name}
						onChange={onFormChange('type_name')}
						placeholder='Ej: Llamada, Reunión, Correo, etc.'
					/>
				</Field>
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={create} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal===Modals.EDIT} onClose={bindClick(setShownModal, null)} size="tiny">
			<Modal.Header text='Editar tipo de actividad' />
			<Modal.Content>
				<Field label='Nombre del tipo'>
					<Input
						fluid
						value={form.type_name}
						onChange={onFormChange('type_name')}
						placeholder='Ej: Llamada, Reunión, Correo, etc.'
					/>
				</Field>
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button text='Guardar' color='black' size='small' onClick={edit} />
			</Modal.Actions>
		</Modal>

		<Modal open={shownModal===Modals.DELETE} onClose={bindClick(setShownModal, null)} size="tiny">
			<Modal.Header text={selectedType?.deleted ? 'Restaurar tipo de actividad' : 'Eliminar tipo de actividad'} />
			<Modal.Content>
				{!!selectedType && (
					<Header
						text={selectedType.deleted ? '¿Restaurar tipo de actividad?' : '¿Eliminar tipo de actividad?'}
						subtext={`Se ${selectedType.deleted ? 'restaurará' : 'eliminará'} el tipo "${selectedType.type_name}"`}
						size='small'
					/>
				)}
				{!selectedType?.deleted && (
					<Message type='warning' list={['Esta acción no se puede deshacer']} />
				)}
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic size='small' onClick={bindClick(setShownModal, null)} />
				<Button
					text={selectedType?.deleted ? 'Restaurar' : 'Eliminar'}
					color={selectedType?.deleted ? 'black' : 'red'}
					size='small'
					onClick={submitToggleDelete}
				/>
			</Modal.Actions>
		</Modal>
	</div>
}

export default ActivityTypesList;
